import { Flex, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import dayjs from 'dayjs'
import { useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  useClassifierServiceGetCountryClassifierElementListEndpoint,
  usePersonHistoriesServiceGetPersonHistoryListEndpoint
} from 'src/api/queries'
import { GetPersonHistoryListResponseItem } from 'src/api/requests'
import { RangePicker, Text } from 'src/components'
import { useTablePagination } from 'src/hooks'
import {
  dayMonthYearFormatWithDot,
  dayMonthYearHourMinutesFormat
} from 'src/libs'
import { useLocales } from 'src/locales'
import { resetPagination } from 'src/utils'

import { useStyles } from '../styles'
import { GenderShortMap } from '../utils'

export const PersonsHistoryPage = () => {
  const { t, currentLang } = useLocales()
  const { styles } = useStyles()
  const { id } = useParams()

  const pagination = useTablePagination()

  const [dateFrom, setDateFrom] = useState<string>()
  const [dateTo, setDateTo] = useState<string>()

  const queryParams = {
    pageNumber: Number(pagination.current),
    pageSize: Number(pagination.pageSize),
    personId: String(id),
    ...(!!dateFrom &&
      !!dateTo && {
        from: dateFrom,
        to: dateTo
      })
  }

  const { data, isLoading } =
    usePersonHistoriesServiceGetPersonHistoryListEndpoint(queryParams)

  const { data: countries } =
    useClassifierServiceGetCountryClassifierElementListEndpoint()

  const columns: ColumnsType<GetPersonHistoryListResponseItem> = [
    {
      key: 'changed at',
      title: t('epidemiology.updated_date'),
      render: (_, { changedAt }) =>
        dayjs.utc(changedAt).tz().format(dayMonthYearHourMinutesFormat)
    },
    {
      key: 'last name',
      title: t('form.last_name'),
      dataIndex: 'lastName'
    },
    {
      key: 'first name',
      title: t('form.first_name'),
      dataIndex: 'firstName'
    },
    {
      key: 'idnp',
      title: t('form.idnp'),
      dataIndex: 'idnp'
    },
    {
      key: 'birth date',
      title: t('form.birth_date'),
      render: (_, { birthDate }) =>
        birthDate ? dayjs(birthDate).format(dayMonthYearFormatWithDot) : '-'
    },
    {
      key: 'phone',
      title: t('common.phone'),
      dataIndex: 'phone'
    },
    {
      key: 'gender',
      title: t('form.gender'),
      render: (_, { gender }) => (gender ? GenderShortMap[gender] : '-')
    },
    {
      key: 'email',
      title: t('form.email'),
      dataIndex: 'email'
    },
    {
      key: 'social status',
      title: t('form.social_status'),
      dataIndex: ['socialStatus', 'translation', currentLang?.value]
    },
    {
      key: 'citizenship',
      title: t('common.citizenship'),
      render: (_, { citizenship }) =>
        countries?.data?.find((country) => country.isoCode === citizenship)?.[
          currentLang?.value
        ]
    },
    {
      key: 'address',
      title: t('other_institutions.address'),
      render: (_, { currentAddress }) =>
        [
          currentAddress?.cuatmLocality?.translation?.[currentLang?.value],
          currentAddress?.street,
          currentAddress?.streetNumber,
          currentAddress?.apartmentNumber
        ]
          .filter(Boolean)
          .join(', ')
    },
    {
      key: 'anonymous',
      title: t('epidemiology.patient_status.anonymous'),
      render: (_, { isAnonymous }) =>
        isAnonymous ? t('common.yes') : t('common.no')
    },
    {
      key: 'investigation status',
      title: t('form.status'),
      render: (_, { investigationStatus }) =>
        investigationStatus && t(`persons.person_status.${investigationStatus}`)
    },
    {
      key: 'institution',
      title: t('common.institution'),
      dataIndex: ['institution', 'translation', currentLang?.value]
    },
    {
      key: 'sime code',
      title: t('common.sime_code'),
      dataIndex: 'simeCode'
    }
  ]

  return (
    <Flex vertical gap={16}>
      <Flex justify="space-between">
        <Text className={styles.text}>{t('persons.person_history')}</Text>

        <RangePicker
          setDateFrom={setDateFrom}
          setDateTo={setDateTo}
          resetPagination={() => resetPagination(pagination)}
        />
      </Flex>

      <Table
        bordered
        dataSource={data?.data}
        columns={columns}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        pagination={{ ...pagination, total: data?.totalCount }}
      />
    </Flex>
  )
}
